import * as React from "react"
import { Autocomplete, Box, CircularProgress, TextField, createFilterOptions } from "@mui/material"
import { FlagsIconSeeker, IconSeeker } from "@ui-seeker/core"

import VirtualizedListbox from "./VirtualizedListbox"

// The synthetic entry the Visibility endpoint puts on top of its catalog. Its value is
// the string 'all', never a number, so a site id can never be tested for truthiness on
// its own — the panels rely on that.
export const ALL_SITES_VALUE = 'all'

const baseFilter = createFilterOptions()

/**
 * The mark that identifies a site: its country flag, or the globe for the catalog-wide
 * entry. Written once because it goes in two places — the dropdown row and the closed
 * box — and the two drifting apart is exactly what makes a selector look wrong.
 *
 * Fixed width so the labels line up whether or not the site has a country.
 */
function SiteMark({ option }) {
    if (!option) return null
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: 20, flexShrink: 0 }}>
            {option.value === ALL_SITES_VALUE
                ? <IconSeeker icon="earth-americas" sx={{ fontSize: 15 }} />
                : option.countryCode && <FlagsIconSeeker countryName={option.countryCode} />}
        </Box>
    )
}

/**
 * Single-select for site catalogs: country flag per row, client-side filtering over the
 * WHOLE catalog, and the same virtualized listbox the multi-select uses — the site lists
 * run to hundreds of rows (417 in visibility, 918 in products) and MUI mounts every
 * option it is given.
 *
 * The multi-select twin of this lives in SitesMultiSelect.jsx and is what
 * products-review uses. This one has no checkboxes, no chips and no Select All: one
 * site, or the catalog-wide entry when the caller passes `allOption`.
 *
 * Expects the FULL catalog in `options` (fetch it with `all=1`), and hands `onChange`
 * the option object — `{value, label, countryCode}` — not an event.
 */
export default function SiteSelect({ options, value, onChange, allOption = false, loading = false,
                                     label = "Site", placeholder = "Search a site…", error = false,
                                     ...props }) {
    const allRow = options.find(o => o.value === ALL_SITES_VALUE)
    const catalog = options.filter(o => o.value !== ALL_SITES_VALUE)

    return <Autocomplete
        size="small"
        disableListWrap
        // The catalog is fetched by the CALLER, unlike the library's AutocompleteAjaxSeeker
        // which loads its own and therefore knows when it is busy. Without this the box
        // just sat there empty while 828 sites were on the wire.
        loading={loading}
        loadingText="Loading sites…"
        ListboxComponent={VirtualizedListbox}
        options={options}
        getOptionLabel={(option) => option?.label || ''}
        isOptionEqualToValue={(option, val) => option.value === val?.value}
        value={value || null}
        // The input text is NOT controlled here, unlike in the multi-select twin. There
        // it is held so the typed filter survives each pick; here the text IS how the
        // choice is shown, and holding it meant swallowing MUI's 'reset' — the site was
        // selected and the box looked empty.
        filterOptions={(opts, state) => {
            const filtered = baseFilter(catalog, state)
            // "All sites" is never filtered out by the typed text: it is the way back to
            // the whole catalog, and hiding it as soon as someone types would mean
            // clearing the box to reach it.
            return allOption && allRow ? [allRow, ...filtered] : filtered
        }}
        onChange={(_, newValue) => onChange(newValue)}
        renderOption={(optionProps, option) => (
            <li {...optionProps} key={option.value}>
                {/* The globe sits where the flags sit, so the rows line up instead of the
                    first one hanging half a column to the left. */}
                <Box sx={{ mr: 0.75, display: 'flex' }}><SiteMark option={option} /></Box>
                <span style={option.value === ALL_SITES_VALUE ? { fontWeight: 600 } : undefined}>
                    {option.label}
                </span>
            </li>
        )}
        renderInput={(params) => (
            <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                size="small"
                // Forwarded explicitly: the {...props} below land on the
                // Autocomplete, so a caller that marks this required had no way
                // to reach the field that actually paints the error.
                error={error}
                InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                        <>
                            {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                            {params.InputProps.endAdornment}
                        </>
                    ),
                    // The chosen site keeps its mark once the dropdown is closed: the row
                    // said "111skin.com (DE)" with a German flag and the box then showed
                    // the text alone.
                    startAdornment: value
                        ? <SiteMark option={value} />
                        : params.InputProps.startAdornment,
                }}
            />
        )}
        {...props}
    />
}
